/**
 * In-memory API mocks for local UI review (dev bypass only).
 * Activated when auth/isDevBypass is true – see src/axios.js.
 */
import {
  MOCK_USER,
  createMockJobs,
  MOCK_RESUME_PREVIEW,
  MOCK_REFERRAL,
  MOCK_PRICES,
  createMockAdminOverview,
  createMockAdminGrowth,
  createMockSuccessCandidates,
} from './fixtures';

let jobs = createMockJobs();
let user = { ...MOCK_USER };

export function resetDevMocks() {
  jobs = createMockJobs();
  user = { ...MOCK_USER };
}

function normalizePath(url, baseURL) {
  let path = (url || '').split('?')[0].trim();
  if (path.startsWith('http')) {
    try {
      path = new URL(path).pathname;
    } catch {
      // keep path
    }
  } else if (baseURL && path.startsWith(baseURL)) {
    path = path.replace(baseURL, '') || '/';
  }
  if (!path.startsWith('/')) path = `/${path}`;
  return path;
}

function findJob(id) {
  return jobs.find((j) => j._id === id);
}

function parseBody(data) {
  if (data == null) return {};
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return data;
}

/**
 * @returns {object|null} Response body, or null if this request should hit the real API.
 */
export function getDevMockResponse(url, method, config = {}) {
  const path = normalizePath(url, config.baseURL);
  const m = (method || 'get').toUpperCase();

  if (m === 'GET' && path === '/api/auth/me') {
    return { user: { ...user } };
  }

  if (m === 'GET' && path === '/api/jobs/stats') {
    const buckets = {
      saved: 0,
      ready: 0,
      applied: 0,
      interviewing: 0,
      offer: 0,
      passed: 0,
      skipped: 0,
    };
    for (const j of jobs) {
      const s = j.status || 'saved';
      if (s === 'applied') buckets.applied += 1;
      else if (s === 'interviewing') buckets.interviewing += 1;
      else if (s === 'offer') buckets.offer += 1;
      else if (s === 'rejected') buckets.passed += 1;
      else if (s === 'skipped') buckets.skipped += 1;
      else if (s === 'resume_generated' || j.hasResume) buckets.ready += 1;
      else buckets.saved += 1;
    }
    return { total: jobs.length, buckets };
  }

  if (m === 'GET' && path === '/api/jobs') {
    const q = config.params || {};
    const page = parseInt(q.page || '1', 10) || 1;
    const limit = parseInt(q.limit || '50', 10) || 50;
    const start = (page - 1) * limit;
    const slice = jobs.slice(start, start + limit).map((j) => ({ ...j }));
    return { jobs: slice, total: jobs.length, page, limit };
  }

  if (m === 'GET' && path === '/api/resume/preview') {
    return { ...MOCK_RESUME_PREVIEW };
  }

  if (m === 'GET' && path === '/api/referral/info') {
    return { ...MOCK_REFERRAL };
  }

  if (m === 'GET' && path === '/api/stripe/prices') {
    return { ...MOCK_PRICES, prices: [...MOCK_PRICES.prices] };
  }

  if (m === 'GET' && path === '/api/admin/analytics/overview') {
    return createMockAdminOverview();
  }

  if (m === 'GET' && path === '/api/admin/analytics/growth') {
    const weeks = parseInt((url || '').split('weeks=')[1], 10) || 12;
    return createMockAdminGrowth(weeks);
  }

  if (m === 'GET' && path === '/api/admin/analytics/success-candidates') {
    return createMockSuccessCandidates();
  }

  if (m === 'GET' && path === '/api/admin/users/inactive') {
    return {
      count: 2,
      users: [
        {
          authUserId: 'inactive-1',
          userId: 'inactive-user-1',
          email: 'new.signup@example.com',
          firstName: 'Taylor',
          lastName: 'Kim',
          daysSinceSignup: 12,
          lastLogin: new Date().toISOString(),
          credits: 3,
          followUpPriority: 'high',
        },
        {
          authUserId: 'inactive-2',
          userId: 'inactive-user-2',
          email: 'trial@example.com',
          firstName: 'Casey',
          lastName: 'Wong',
          daysSinceSignup: 4,
          lastLogin: null,
          credits: 3,
          followUpPriority: 'medium',
        },
      ],
    };
  }

  if (m === 'GET' && path === '/api/admin/users') {
    const data = createMockSuccessCandidates();
    return {
      count: data.candidates.length,
      users: data.candidates.map((c) => ({
        authUserId: c.userId,
        userId: c.userId,
        email: c.email,
        firstName: c.firstName,
        lastName: c.lastName,
        hasResume: true,
        credits: c.credits,
        jobCount: c.jobCount,
        resumesGenerated: c.resumesGenerated,
      })),
    };
  }

  const adminUserMatch = path.match(/^\/api\/admin\/users\/([^/]+)$/);
  if (adminUserMatch && m === 'GET') {
    const c = createMockSuccessCandidates().candidates[0];
    return {
      userId: adminUserMatch[1],
      authUserId: adminUserMatch[1],
      email: c.email,
      firstName: c.firstName,
      lastName: c.lastName,
      credits: c.credits,
      hasResume: true,
      jobCount: c.jobCount,
      resumesGenerated: c.resumesGenerated,
      signupDate: c.signupDate,
      lastLogin: c.lastLogin,
    };
  }

  const adminUserJobsMatch = path.match(/^\/api\/admin\/users\/([^/]+)\/jobs$/);
  if (adminUserJobsMatch && m === 'GET') {
    return {
      count: 1,
      jobs: [
        {
          _id: 'mock-job-detail',
          title: 'Senior PM',
          companyName: 'Acme Corp',
          status: 'interviewing',
          hasResume: true,
          compatibilityScore: {
            overallScore: 82,
            overallVerdict: 'Good Match',
            highlights: ['Product launch experience', 'B2B SaaS background'],
            gaps: ['Limited enterprise sales exposure'],
          },
          resumeTextPreview: 'Experienced product manager with 8+ years driving B2B roadmap...',
          resumeTextLength: 2400,
        },
      ],
    };
  }

  const adminCreditsMatch = path.match(/^\/api\/admin\/users\/([^/]+)\/credits$/);
  if (adminCreditsMatch && m === 'PATCH') {
    const body = parseBody(config.data);
    return {
      userId: adminCreditsMatch[1],
      creditsBefore: 8,
      creditsAfter: 8 + (body.amount || 0),
      adjustment: body.amount || 0,
    };
  }

  const jobStatusMatch = path.match(/^\/api\/jobs\/([^/]+)\/status$/);
  if (jobStatusMatch) {
    const job = findJob(jobStatusMatch[1]);
    if (!job) return { message: 'Job not found' };
    const body = parseBody(config.data);
    if (m === 'PATCH') {
      if (body.status != null) job.status = body.status;
      if (body.notes != null) job.notes = body.notes;
      return { job: { ...job } };
    }
  }

  const jobDeleteMatch = path.match(/^\/api\/jobs\/([^/]+)$/);
  if (jobDeleteMatch && m === 'DELETE') {
    const idx = jobs.findIndex((j) => j._id === jobDeleteMatch[1]);
    if (idx === -1) return { message: 'Job not found' };
    jobs.splice(idx, 1);
    return { success: true };
  }

  if (m === 'POST' && path === '/api/resume/generate') {
    const body = parseBody(config.data);
    const jobId = body.jobData?._id;
    const job = jobId ? findJob(jobId) : null;
    if (job) {
      job.hasResume = true;
      if (job.status === 'saved') job.status = 'resume_generated';
    }
    if (user.credits > 0) user.credits -= 1;
    return { jobId: jobId || 'dev-job-mock', creditsRemaining: user.credits };
  }

  if (m === 'PUT' && path === '/api/auth/profile') {
    const body = parseBody(config.data);
    Object.assign(user, body);
    return { user: { ...user } };
  }

  if (m === 'POST' && path === '/api/stripe/create-checkout-session') {
    return { url: null, devMock: true, message: 'Stripe checkout disabled in dev mock mode.' };
  }

  if (m === 'POST' && path === '/api/support/bug-report') {
    return { success: true, devMock: true };
  }

  const downloadMatch = path.match(/^\/api\/resume\/([^/]+)\/download\/(pdf|docx|html)$/);
  if (downloadMatch && m === 'GET') {
    return {
      devMock: true,
      format: downloadMatch[2],
      message: 'Download mocked in dev mode – no file generated.',
    };
  }

  return null;
}
